import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';
import { loadNext as loadCollectionNext } from '@theme/wuu-tabs-collection-request';
import { loadNext as loadApiNext, setEmptyVisible as setApiEmptyVisible } from '@theme/wuu-tabs-api-request';

/**
 * Tab UI + sticky + infinite-scroll dispatcher for collection / API tabs.
 *
 * @typedef {object} WuuTabsProductListRefs
 * @property {HTMLElement} [tablist]
 * @property {HTMLElement} [tabSticky]
 * @property {HTMLElement} [loadMore]
 *
 * @extends {Component<WuuTabsProductListRefs>}
 */
class WuuTabsProductList extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {boolean} */
  #loading = false;

  connectedCallback() {
    super.connectedCallback();
    this.#mountTabStrip();
    this.#ensureActiveTab();
    this.#ensureLoadMore();
    this.#observeLoadMore();
    this.#maybeBootstrapApiTab();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#controller?.abort();
  }

  /**
   * Moves focus and selection with arrow / Home / End keys.
   *
   * @param {KeyboardEvent} event
   */
  handleTabKeydown(event) {
    const tabs = this.#tabs;
    if (!tabs.length) return;

    const currentIndex = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextTab = tabs[nextIndex];
    if (!nextTab) return;

    this.#activateTab(nextTab, {
      scrollTop: true
    });
    nextTab.focus({
      preventScroll: true
    });
  }

  /**
   * Activates the tab that received a click or Enter / Space.
   *
   * @param {MouseEvent | KeyboardEvent} event
   */
  selectTab(event) {
    if (event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ') return;

    const button = this.#eventTab(event);
    if (!button) return;

    this.#activateTab(button, {
      scrollTop: true
    });
  }

  /** Selects the first tab when none is marked active. */
  #ensureActiveTab() {
    if (this.#tabs.some((tab) => tab.getAttribute('aria-selected') === 'true')) return;

    const first = this.#tabs[0];
    if (!first) return;

    this.#activateTab(first, {
      scroll: false,
      scrollTop: false
    });
  }

  /**
   * Shows the matching panel and updates ARIA selection state.
   *
   * @param {HTMLButtonElement} button
   * @param {object} [options]
   * @param {boolean} [options.scroll=true] - Scroll the active tab into the strip
   * @param {boolean} [options.scrollTop=false] - Scroll the page so the tab bar pins to the top
   */
  #activateTab(button, {
    scroll = true,
    scrollTop = false
  } = {}) {
    const tabId = button.getAttribute('aria-controls');
    if (!tabId) return;

    for (const tab of this.#tabs) {
      const selected = tab === button;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
    }

    for (const panel of this.#panels) {
      const match = panel.id === tabId;
      panel.toggleAttribute('hidden', !match);
    }

    if (scroll) this.#scrollTabStrip(button);
    if (scrollTop) this.#scrollTabTop();
    this.#ensureLoadMore();
    this.#maybeBootstrapApiTab();
  }

  /**
   * Moves tab buttons from theme blocks into the horizontal scroll strip.
   * Restores page scroll if DOM moves shifted the viewport.
   */
  #mountTabStrip() {
    const tablist = this.refs.tablist;
    if (!tablist) return;

    const pageScrollTop = getScrollTop();
    const fragment = document.createDocumentFragment();
    let moved = false;

    for (const tab of this.querySelectorAll('.wuu-tabs-product-list__tab')) {
      if (tab.parentElement !== tablist) {
        fragment.append(tab);
        moved = true;
      }
    }

    if (!moved) return;
    tablist.append(fragment);

    if (Math.abs(getScrollTop() - pageScrollTop) > 2) {
      scrollTo({
        top: pageScrollTop,
        behavior: 'instant'
      });
    }
  }

  /**
   * Centers the active tab in the strip without scrolling the page.
   *
   * @param {HTMLButtonElement} button
   */
  #scrollTabStrip(button) {
    const scroller = button.closest('.wuu-tabs-product-list__scroll');
    if (!scroller) return;
    const scrollerRect = scroller.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    const delta = buttonRect.left - scrollerRect.left - (scrollerRect.width - buttonRect.width) / 2;
    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const next = Math.min(maxScroll, Math.max(0, scroller.scrollLeft + delta));
    scroller.scrollTo({
      left: next,
      top: scroller.scrollTop,
      behavior: 'smooth'
    });
  }

  /**
   * Scrolls the page so the sticky tab bar sits under the header
   * (or at the viewport top). Skipped in the theme editor.
   */
  #scrollTabTop() {
    const sticky = this.refs.tabSticky;
    if (!sticky || window.Shopify?.designMode) return;

    const destTop = this.#predictedTabStickyTop(sticky);
    const delta = sticky.getBoundingClientRect().top - destTop;
    if (Math.abs(delta) < 8) return;

    scrollTo({
      top: Math.max(0, getScrollTop() + delta),
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    });
  }

  /**
   * Target `top` for the sticky tab bar, accounting for header sticky mode.
   *
   * @param {HTMLElement} sticky
   * @returns {number}
   */
  #predictedTabStickyTop(sticky) {
    const header = document.querySelector('header-component');
    const headerHeight =
      Number.parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    const mode = header?.getAttribute('sticky');
    const distance = sticky.getBoundingClientRect().top;
    // scroll-up headers hide on downward scroll; pin to the viewport top instead.
    if (mode === 'scroll-up' && distance > headerHeight + 8) return 0;
    if (mode === 'always' || header?.dataset.stickyState === 'active') return headerHeight;
    return 0;
  }

  /** Watches the load-more sentinel and fetches the next page when it nears the viewport. */
  #observeLoadMore() {
    const sentinel = this.refs.loadMore;
    if (!sentinel) return;
    this.#observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        this.#loadNextPage();
      }
    }, {
      rootMargin: '200px'
    });
    this.#observer.observe(sentinel);
  }

  /**
   * Shows or hides the load-more sentinel from the active panel's `data-has-more`.
   */
  #ensureLoadMore() {
    const sentinel = this.refs.loadMore;
    const panel = this.#activePanel;
    if (!sentinel) return;
    const hasMore = panel?.dataset.hasMore === 'true';
    sentinel.toggleAttribute('hidden', !hasMore);
  }

  /**
   * API tabs SSR an empty grid — kick the first page when the tab becomes active.
   */
  #maybeBootstrapApiTab() {
    const panel = this.#activePanel;
    if (!panel || panel.dataset.source !== 'api') return;
    if (panel.dataset.hasMore !== 'true') return;

    const grid = panel.querySelector('[data-wuu-tabs-grid]');
    if (grid?.querySelector('[data-wuu-tabs-product-item]')) return;

    this.#loadNextPage();
  }

  /**
   * Dispatches the next page load to collection or API request modules.
   */
  async #loadNextPage() {
    const panel = this.#activePanel;
    const sentinel = this.refs.loadMore;

    if (this.#loading || !panel || panel.dataset.hasMore !== 'true') return;

    this.#loading = true;
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.setAttribute('aria-busy', 'true');
    sentinel?.setAttribute('data-loading', '');

    try {
      const source = panel.dataset.source || 'collection';
      const ctx = { signal: this.#controller.signal };

      if (source === 'api') {
        await loadApiNext(panel, ctx);
        // API first page is client-fetched; when infinite scroll is off, stop after one page.
        if (this.getAttribute('data-infinite-scroll') !== 'true') {
          panel.dataset.hasMore = 'false';
        }
      } else {
        await loadCollectionNext(panel, ctx);
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
      panel.dataset.hasMore = 'false';
      if (
        panel.dataset.source === 'api' &&
        !panel.querySelector('[data-wuu-tabs-product-item]')
      ) {
        setApiEmptyVisible(panel, true);
      }
    } finally {
      this.#loading = false;
      this.removeAttribute('aria-busy');
      sentinel?.removeAttribute('data-loading');
      this.#ensureLoadMore();
    }
  }

  /** @returns {HTMLButtonElement[]} */
  get #tabs() {
    return [
      ...this.querySelectorAll('.wuu-tabs-product-list__tab')
    ];
  }

  /** @returns {HTMLElement[]} */
  get #panels() {
    return [
      ...this.querySelectorAll('.wuu-tabs-product-list__panel')
    ];
  }

  /** @returns {HTMLElement | null} */
  get #activePanel() {
    return this.#panels.find((panel) => !panel.hasAttribute('hidden')) || null;
  }

  /**
   * Resolves the tab button that owns the event target.
   *
   * @param {Event} event
   * @returns {HTMLButtonElement | null}
   */
  #eventTab(event) {
    const target = event.target;
    if (!(target instanceof Element)) return null;
    return target.closest('[role="tab"]');
  }
}

if (!customElements.get('wuu-tabs-product-list-component')) {
  customElements.define('wuu-tabs-product-list-component', WuuTabsProductList);
}
